import React from 'react';
import { Box, Button, Theme, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { ChevronRight, VisibilityOff, WarningRounded, Visibility } from '@material-ui/icons';
import { unpackToDate } from '@campfire/hot-date';
import { DraggableListItem, DraggableListItemHandle } from '@campfire/draggable-list';
import { GetTaskDashboard_vm_tasks as DashTaskType } from '../Dashboard/__generated__/GetTaskDashboard';
import { TaskListGetCake_vm_cake_allTasks as CakeTaskType } from '../TasksList/__generated__/TaskListGetCake';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { TaskBadge } from '../TaskBadge';

type TaskType = DashTaskType | CakeTaskType;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: '24px',
      paddingLeft: '24px',
      paddingTop: '16px',
      paddingBottom: '16px',
      border: '1px solid #54545440',
      borderRadius: '4px',
      justifyContent: 'space-between',
      cursor: 'pointer',
      backgroundColor: theme.color.white.neutral50,
      flex: 1,
    },
    taskContainer: {
      '& .title': {
        fontSize: '24px',
        fontWeight: 800,
        color: theme.color.grey.neutral400,
      },
      '& .description': {
        fontWeight: 400,
        color: theme.color.grey.neutral400,
        fontSize: '0.9rem',
      },
      '& .badge': {
        fontSize: '0.75rem',
        fontWeight: 550,
        padding: 8,
        paddingLeft: 12,
        paddingRight: 12,
        borderRadius: 4,
        backgroundColor: '#D9D9D9',
        color: theme.color.grey.neutral400,
      },
      '& .badge.userCompletes': {
        fontWeight: 600,
      },
      '& .badge.managerCompletes': {
        fontWeight: 600,
      },
      '& .badgeContainer': {
        display: 'flex',
        marginTop: '8px',
        gap: '8px',
      },
      '& .expiry': {
        color: '#92400E',
        backgroundColor: '#FFFBEB',
        display: 'flex',
        alignItems: 'center',
      },
      '& .expiryIcon': {
        color: '#FBBF24',
        fontSize: '12px',
        marginRight: '8px',
      },
    },
    button: {
      paddingLeft: 0,
      paddingRight: 0,
      minWidth: 'unset',
      width: '36px',
      color: 'white',
      backgroundColor: '#9E9E9E',
    },
  })
);

interface TaskProps {
  task: TaskType;
  onClick: () => void;
}

export function Task({ onClick, task }: TaskProps) {
  const classes = useStyles();
  const { theme } = useCampfireTheme();

  const isExpiring =
    'expiryDate' in task
      ? task.expiryDate &&
        (unpackToDate(task.expiryDate)
          .diffNow('weeks')
          .toObject().weeks || 0) < 2
      : false;

  return (
    <Box className={classes.container} onClick={onClick}>
      <Box className={classes.taskContainer}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
          <Typography
            variant={'h6'}
            style={{ color: theme.color.grey.neutral400, fontWeight: 700, letterSpacing: '-0.02em', flexShrink: 0 }}
          >
            {task.title}
          </Typography>
          {task.__typename === 'VOLUNTEER_TaskType' && task?.metadata && task?.metadata.totalPendingApprovals ? (
            <TaskBadge number={task.metadata.totalPendingApprovals} variant='warning' />
          ) : null}
          <Typography
            className='description'
            style={{
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {task.description}
          </Typography>
        </Box>
        <Box className={'badgeContainer'} style={{ gap: 9 }}>
          {task.isRefereeCheckTask ? (
            <Box className='badge' style={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}>
              Referee Check
            </Box>
          ) : task.isEventTask ? (
            <Box className='badge' style={{ backgroundColor: '#e0f2f1', color: '#00695c' }}>
              Event Task
            </Box>
          ) : (task as any).isPaymentTask || (task as any).stripeConfig ? (
            <Box className='badge' style={{ backgroundColor: '#fff3e0', color: '#ef6c00' }}>
              Payment Task
            </Box>
          ) : (
            <Box
              className={`badge ${task.managerCompleted ? 'managerCompletes' : 'userCompletes'}`}
              style={{
                backgroundColor: task.managerCompleted ? '#FFF4E6' : '#E3F2FD',
                color: task.managerCompleted ? '#E65100' : '#1565C0',
              }}
            >
              {task.managerCompleted ? 'Manager Completes' : 'User Completes'}
            </Box>
          )}
          {!task.automateApproval && !task.isEventTask && <Box className='badge'>Manual Review</Box>}
          {(task.programs.length > 0 || task.roles.length > 0) && <Box className='badge'>Restricted Visibility</Box>}
          {isExpiring && (
            <Box className='badge expiry'>
              <WarningRounded className='expiryIcon' />
              Expiring Soon
            </Box>
          )}
          {task.recurrences.length > 0 && <Box className='badge'>Expiry Enabled</Box>}
          {task.allowUpdate && <Box className='badge'>Updating Allowed</Box>}
          {task.applicantsOnly && !task.isEventTask && !task.isRefereeCheckTask && (
            <Box className='badge'>Applicants Only</Box>
          )}
          {task.adminOnly && (
            <Tooltip title='Task submissions are viewable by admins only'>
              <Box className='badge' display={'flex'} alignItems={'center'}>
                <Visibility style={{ fontSize: '15px', paddingRight: '5px' }} /> Admin Only
              </Box>
            </Tooltip>
          )}
          {task.isHidden && (
            <Box className='badge' display={'flex'} alignItems={'center'}>
              <VisibilityOff style={{ fontSize: '15px', paddingRight: '5px' }} /> Hidden
            </Box>
          )}
        </Box>
      </Box>
      <Button variant='contained' disableElevation className={classes.button}>
        <ChevronRight />
      </Button>
    </Box>
  );
}

type DraggableTaskProps = TaskProps & {
  index: number;
};

export function DraggableTask({ onClick, task, index }: DraggableTaskProps) {
  return (
    <DraggableListItem id={`${index}`} index={index}>
      <Box display='flex' flex='1 1 auto' position='relative'>
        <Box marginRight='2rem' marginLeft='1rem'>
          <DraggableListItemHandle />
        </Box>

        <Box display='flex' flex='1 1 auto' alignContent='center' alignItems='center'>
          <Task onClick={onClick} task={task} />
        </Box>
      </Box>
    </DraggableListItem>
  );
}
